import React from "react";
import { Image } from "react-bootstrap";
import {
  Tournament,
  TournamentParticipant,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./Result.scss";
import { type IOpponentSummary } from "../../../jotai/atoms/tournament";
import { GameOutcome as Outcome } from "../../../utils/constants";
import ImageCoinFlat from "../../../images/coin-flat.svg";
import ImageCoinStack from "../../../images/coin-stack.png";
import ParticipantResult from "../ParticipantResult/ParticipantResult";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface ResultProps {
  userOutcome?: Outcome;
  opponentOutcome?: Outcome;
  duelOpponent: IOpponentSummary | null;
  headingText: string;
  canShowReward?: boolean;
  tournamentDetails?: Tournament;
  userInfo?: TournamentParticipant;
  opponentInfo?: TournamentParticipant;
  hasGameFailed?: boolean;
}

const getColorByOutcome = (userOutcome?: Outcome) => {
  switch (userOutcome) {
    case Outcome.LOSS:
      return "#E3E3E3";
    case Outcome.AWAITING_OPPONENT_SCORE:
    case Outcome.OPPONENT_CANCELLED:
    case Outcome.WAITING_TO_BE_MATCHED:
      return "white";
    default:
      return undefined;
  }
};

function Result({
  userOutcome,
  canShowReward,
  headingText,
  opponentInfo,
  userInfo,
  duelOpponent,
  tournamentDetails,
  opponentOutcome,
  hasGameFailed,
}: ResultProps) {
  const { t } = useTranslation("games");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  const formattedReward = isFiatDisplay
    ? formatHprToFiatValue(tournamentDetails?.rewardAmount ?? 0)
    : tournamentDetails?.rewardAmount;

  return (
    <>
      <div className="result">
        <p className="d-flex justify-content-center mb-2">
          <Image
            src={tournamentDetails?.game?.thumbnail}
            alt="game banner"
            width={34}
          />
        </p>

        <p className="game-title slashed-zero text-uppercase">
          {tournamentDetails?.game?.name}
        </p>
        {!hasGameFailed && (
          <>
            {userOutcome === Outcome.OPPONENT_CANCELLED && (
              <p className="cancelled">
                @{duelOpponent?.username} {t("cancelled")}
              </p>
            )}
            <h1
              className="main-heading slashed-zero"
              style={{
                color: getColorByOutcome(userOutcome),
              }}
            >
              {headingText}
            </h1>
            {canShowReward && (
              <>
                <p className="subhead slashed-zero golden">
                  {t("your_reward")}
                </p>
                {userOutcome === Outcome.LOSS ? (
                  <div className="d-flex gap-3 mx-auto w-content">
                    {!isFiatDisplay && (
                      <Image
                        src={ImageCoinFlat}
                        alt="coin"
                        className="mx-auto"
                      />
                    )}
                    <p
                      className={`slashed-zero ${
                        isFiatDisplay ? "fancy-text-fiat" : "fancy-text"
                      }`}
                    >
                      {isFiatDisplay ? (
                        <span className="d-inline-flex" style={{ gap: 2 }}>
                          <span>{formatHprToFiatValue(0).charAt(0)}</span>
                          <span>{formatHprToFiatValue(0).slice(1)}</span>
                        </span>
                      ) : (
                        "0"
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="mx-auto" style={{ width: "fit-content" }}>
                    {!isFiatDisplay && (
                      <Image src={ImageCoinStack} width={60} alt="coin stack" />
                    )}
                    <p
                      className={`slashed-zero ${
                        isFiatDisplay
                          ? "fancy-text-fiat golden"
                          : "fancy-text winnings"
                      }`}
                    >
                      {isFiatDisplay ? (
                        <span className="d-inline-flex" style={{ gap: 2 }}>
                          <span>{formattedReward?.toString().charAt(0)}</span>
                          <span>{formattedReward?.toString().slice(1)}</span>
                        </span>
                      ) : (
                        formattedReward
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {!hasGameFailed && (
        <div
          className="result d-flex align-items-center justify-content-evenly position-relative "
          style={{ top: canShowReward ? -30 : undefined }}
        >
          <ParticipantResult
            outcome={userOutcome}
            isLoggedInUser
            username={userInfo?.username}
            avatar={userInfo?.avatarUrl}
            score={userInfo?.score}
          />
          <p className="versus">{t("versus")}</p>
          <ParticipantResult
            outcome={opponentOutcome}
            username={opponentInfo?.username}
            avatar={opponentInfo?.avatarUrl}
            score={opponentInfo?.score}
          />
        </div>
      )}
    </>
  );
}

export default Result;
